import React, { useCallback, useEffect, useState } from "react";
import { isLoggedIn } from "../utils";
import { Link, useHistory } from "react-router-dom";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import ToolkitProvider from "react-bootstrap-table2-toolkit";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  declineQuote,
  deleteQuote,
  listQuotes,
} from "../services/QuoteServices";
import Moment from "react-moment";
import { toast } from "react-toastify";
import { Spinner } from "reactstrap";
import { quoteFilters } from "../constants/quoteFilters";
import ConfirmationModal from "../components/common/ConfirmationModal";

export default function QuoteListing() {
  const history = useHistory();
  const [quotes, setQuotes] = useState([]);
  const [filteredQuotes, setFilteredQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedQuoteId, setSelectedQuoteId] = useState(null);
  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  const [declineLoading, setDeclineLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);

  useEffect(() => {
    if (!isLoggedIn()) {
      history.push("/login");
    }
  }, [history]);

  const handleList = useCallback(() => {
    setLoading(true);
    listQuotes()
      .then((res) => {
        console.log("Quotes fetched", res.data);
        setQuotes(res.data);
        setFilteredQuotes(res.data);
      })
      .catch((err) => {
        console.error("Error fetching quotes", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    handleList();
  }, [handleList]);

  const formatDateToYYYYMMDD = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    } catch (error) {
      return "";
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    if (date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      setDateFilter(`${year}-${month}-${day}`);
    } else {
      setDateFilter("");
    }
  };

  useEffect(() => {
    let filtered = [...quotes];

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((quote) => {
        const quoteStatus = (quote.status || "").toLowerCase().trim();
        const filterValue = statusFilter.toLowerCase().trim();
        return quoteStatus === filterValue;
      });
    }

    // Apply date filter
    if (dateFilter) {
      filtered = filtered.filter((quote) => {
        const createdDate = formatDateToYYYYMMDD(quote.start_date);
        const expiryDate = formatDateToYYYYMMDD(quote.expiry_date);
        const validDate = formatDateToYYYYMMDD(quote.valid_date);
        return (
          createdDate === dateFilter ||
          expiryDate === dateFilter ||
          validDate === dateFilter
        );
      });
    }

    setFilteredQuotes(filtered);
  }, [statusFilter, dateFilter, quotes]);

  // Sync selectedDate when dateFilter changes externally
  useEffect(() => {
    if (dateFilter) {
      const date = new Date(dateFilter);
      if (!isNaN(date.getTime())) {
        setSelectedDate(date);
      }
    } else {
      setSelectedDate(null);
    }
  }, [dateFilter]);

  const getStatusName = (statusValue) => {
    if (!statusValue) return statusValue;

    // Normalize the status value: lowercase, trim, replace spaces with underscores
    const normalizedStatus = statusValue
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "_");

    // Find matching filter
    const filter = quoteFilters.find(
      (f) => f.value.toLowerCase() === normalizedStatus
    );

    return filter ? filter.name : statusValue;
  };

  const handleDelete = (id) => {
    setSelectedQuoteId(id);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!selectedQuoteId) return;

    setDeleteLoading(true);
    deleteQuote(selectedQuoteId)
      .then((res) => {
        console.log("Quote deleted", res.data);
        toast.success("Quote deleted successfully");
        setDeleteModalOpen(false);
        setSelectedQuoteId(null);
        handleList();
      })
      .catch((err) => {
        console.error("Error deleting quote", err);
        toast.error("Failed to delete quote");
      })
      .finally(() => {
        setDeleteLoading(false);
      });
  };

  const handleDecline = (id) => {
    setSelectedQuoteId(id);
    setDeclineModalOpen(true);
  };

  const handleDeclineConfirm = () => {
    if (!selectedQuoteId) return;

    setDeclineLoading(true);
    declineQuote(selectedQuoteId)
      .then((res) => {
        console.log("Quote declined", res.data);
        toast.success("Quote declined successfully");
        setDeclineModalOpen(false);
        setSelectedQuoteId(null);
        handleList();
      })
      .catch((err) => {
        console.error("Error declining quote", err);
        toast.error("Failed to decline quote");
      })
      .finally(() => {
        setDeclineLoading(false);
      });
  };

  const handleAccept = (id) => {
    setSelectedQuoteId(id);
    setAcceptModalOpen(true);
  };

  const handleAcceptConfirm = () => {
    if (!selectedQuoteId) return;
    history.push(`/quote/checkout/${selectedQuoteId}`);
  };

  const columns = [
    {
      dataField: "quote_id",
      text: "Quote ID",
    },
    {
      dataField: "start_date",
      text: "Date Requested",
      formatter: (cell, row) =>
        cell ? <Moment format="MM/DD/YYYY">{cell}</Moment> : "-",
    },
    {
      dataField: "item_count",
      text: "No. of items",
      headerClasses: "quote-item-count-header",
      classes: "quote-item-count-cell",
    },
    {
      dataField: "total_amount",
      text: "Quoted Price",
      headerClasses: "quote-price-header",
      classes: "quote-price-cell",
      formatter: (cell, row) => {
        return cell > 0 ? (
          <div>
            $
            {parseFloat(cell).toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </div>
        ) : (
          "-"
        );
      },
    },
    {
      dataField: "status",
      text: "Status",
      formatter: (cell, row) => {
        const statusName = getStatusName(cell);
        const statusValue = (cell || "")
          .toLowerCase()
          .trim()
          .replace(/\s+/g, "_");
        return (
          <span className={`badge badge-${statusValue}`}>{statusName}</span>
        );
      },
    },
    {
      dataField: "valid_date",
      text: "Accept Quote By Date",
      formatter: (cell, row) =>
        cell ? <Moment format="MM/DD/YYYY">{cell}</Moment> : "-",
    },
    {
      dataField: "expiry_date",
      text: "Expiring Date",
      formatter: (cell, row) => <Moment format="MM/DD/YYYY">{cell}</Moment>,
    },
    {
      dataField: "quote_id",
      text: "Action",
      headerClasses: "quote-action-header",
      classes: "quote-action-cell",
      formatter: (cell, row) => {
        const status = (row.status || "").toLowerCase().trim();
        const isDraft = status === "draft";
        const isAssignedToCustomer = status === "assigned_to_customer";

        return (
          <div className="quote-action-buttons">
            <button
              className="btn btn-sm btn-primary "
              onClick={() => history.push(`/quote/${cell}`)}
            >
              View
            </button>

            {isDraft && (
              <>
                <button
                  className="btn btn-sm btn-success"
                  onClick={() => history.push(`/quote/edit/${cell}`)}
                >
                  Edit
                </button>
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => handleDelete(cell)}
                >
                  Delete
                </button>
              </>
            )}

            {isAssignedToCustomer && (
              <>
                <button
                  className="btn btn-sm btn-success"
                  type="button"
                  onClick={() => handleAccept(cell)}
                >
                  Accept
                </button>
                <button
                  className="btn btn-sm btn-danger"
                  type="button"
                  onClick={() => handleDecline(cell)}
                >
                  Decline
                </button>
              </>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="quote-listing-page">
      <div className="quote-header">
        <h3>Quote Listing</h3>
        <Link to="/quote/create">
          <button className=" create-quote-button">Create new quote</button>
        </Link>
      </div>
      {/* Quote Filters */}
      <div className="quote-filters">
        <div className="quote-filter-item quote-filter-status">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {quoteFilters.map((filter) => (
              <option key={filter.id} value={filter.value}>
                {filter.name}
              </option>
            ))}
          </select>
        </div>
        <div className="quote-filter-item datepicker-wrapper">
          <DatePicker
            selected={selectedDate}
            onChange={handleDateChange}
            dateFormat="MM/dd/yyyy"
            placeholderText="MM/DD/YYYY"
            isClearable
            className="form-control"
          />
          <i className="fa fa-calendar datepicker-icon" />
        </div>
      </div>
      {/* Quote List */}
      <div className="quote-list">
        {loading ? (
          <div className="quote-loading">
            <Spinner animation="border" />
            <p>Loading quotes...</p>
          </div>
        ) : filteredQuotes.length === 0 ? (
          <div className="view-quote-empty">
            <p>
              {quotes.length === 0
                ? "No quotes found."
                : `No quotes found with the selected filters.`}
            </p>
          </div>
        ) : (
          <ToolkitProvider
            keyField="quote_id"
            data={filteredQuotes || []}
            columns={columns}
          >
            {(props) => (
              <BootstrapTable
                {...props.baseProps}
                keyField="quote_id"
                data={filteredQuotes || []}
                columns={columns}
                bordered={false}
                pagination={paginationFactory({
                  sizePerPage: 10,
                  showTotal: true,
                  hideSizePerPage: true,
                })}
              />
            )}
          </ToolkitProvider>
        )}
      </div>

      <ConfirmationModal
        isOpen={declineModalOpen}
        onClose={() => {
          setDeclineModalOpen(false);
          setSelectedQuoteId(null);
        }}
        title="Decline Quote"
        content="Are you sure you want to decline this quote? This action cannot be undone."
        primaryAction={{
          label: "Yes, Decline",
          color: "danger",
          onClick: handleDeclineConfirm,
          loading: declineLoading,
        }}
        secondaryAction={{
          label: "Cancel",
        }}
      />

      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedQuoteId(null);
        }}
        title="Delete Quote"
        content="Are you sure you want to delete this quote? This action cannot be undone."
        primaryAction={{
          label: "Yes, Delete it",
          color: "danger",
          onClick: handleDeleteConfirm,
          loading: deleteLoading,
        }}
        secondaryAction={{
          label: "Cancel",
        }}
      />

      <ConfirmationModal
        isOpen={acceptModalOpen}
        onClose={() => {
          setAcceptModalOpen(false);
          setSelectedQuoteId(null);
        }}
        title="Accept Quote"
        content="Are you sure you want to accept this quote?"
        primaryAction={{
          label: "Yes, Accept",
          color: "success",
          onClick: handleAcceptConfirm,
        }}
        secondaryAction={{
          label: "Cancel",
        }}
      />
    </div>
  );
}
